import { useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Input,
  Typography,
  Alert,
  theme,
  Flex,
  Avatar,
  Progress,
  Row,
  Col,
} from 'antd';
import {
  RollbackOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useRefundPaymentMutation } from '../api';
import type { Payment } from '../api';
import useAppNotification from '@/hooks/useAppNotification';
import { useTranslation } from 'react-i18next';
import { formModalWidth } from '@/components/ui/formModalStyles';
import { FormModalFooter } from '@/components/ui/FormModalFooter';
import { ModalTitle } from '@/components/ui/ModalTitle';
import { getApiErrorMessage } from '@/utils/apiError';
import { formatCurrency } from '@/utils/format';
import { getInitials } from '@/utils/userDisplay';

const { Text } = Typography;
const { TextArea } = Input;
const { useToken } = theme;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Quick-select percentage options for the refund amount. */
const REFUND_PERCENTAGE_OPTIONS = [25, 50, 75, 100] as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RefundPaymentModalProps {
  open: boolean;
  onClose: () => void;
  payment: Payment | null;
  onSuccess?: () => void;
}

interface RefundFormValues {
  amount: number;
  description: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Modal for processing partial or full refunds on a completed payment.
 *
 * Features:
 * - Pre-fills with the original payment amount.
 * - Quick-select buttons (25 / 50 / 75 / 100 %) for common refund amounts.
 * - Live summary bar showing partial vs full refund with a progress indicator.
 * - Requires a textual reason before submitting.
 */
export function RefundPaymentModal({
  open,
  onClose,
  payment,
  onSuccess,
}: RefundPaymentModalProps) {
  const { token } = useToken();
  const { t } = useTranslation('payment-module');
  const [form] = Form.useForm<RefundFormValues>();
  const { showSuccessMessage, showErrorMessage } = useAppNotification();
  const refundMutation = useRefundPaymentMutation();

  // -------------------------------------------------------------------------
  // Side effects
  // -------------------------------------------------------------------------

  /** Reset the form whenever the modal opens with a (potentially different) payment. */
  useEffect(() => {
    if (open && payment) {
      form.resetFields();
      form.setFieldsValue({
        amount: payment.amount,
        description: '',
      });
    }
  }, [open, payment, form]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  /**
   * Validates the form, sends the refund request, and closes the modal on success.
   * Displays a formatted success / error notification.
   */
  const handleSubmit = useCallback(async () => {
    if (!payment) return;

    try {
      const values = await form.validateFields();

      await refundMutation.mutateAsync({
        token: payment.token,
        data: {
          amount: values.amount,
          description: values.description,
        },
      });

      showSuccessMessage(
        t('refund.successMessage', { amount: formatCurrency(values.amount, payment.currency) }),
      );
      onClose();
      onSuccess?.();
    } catch (error: unknown) {
      console.error('Refund failed:', error);
      const errorMessage = getApiErrorMessage(error, t('refund.errorMessage'));
      showErrorMessage(errorMessage);
    }
  }, [payment, form, refundMutation, showSuccessMessage, showErrorMessage, onClose, onSuccess]);

  /** Resets the form and closes the modal without submitting. */
  const handleClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  // -------------------------------------------------------------------------
  // Memoised styles (depend on the theme token)
  // -------------------------------------------------------------------------

  const customerBannerStyle = useMemo(
    () => ({
      background: token.colorBgLayout,
      borderRadius: token.borderRadius,
      padding: `${token.paddingXS}px ${token.paddingSM}px`,
      marginBottom: token.marginSM,
    }),
    [token],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Modal
      title={<ModalTitle icon={<RollbackOutlined />} title={t('refund.title')} />}
      open={open}
      footer={null}
      onCancel={handleClose}
      width={formModalWidth(580)}
      destroyOnHidden
    >
      {payment && (
        <div style={{ marginTop: token.marginXS }}>
          {/* Customer identity and original payment amount */}
          <Flex
            justify="space-between"
            align="center"
            style={customerBannerStyle}
          >
            <Flex gap={token.marginXS} align="center">
              <Avatar
                size={32}
                style={{
                  backgroundColor: token.colorPrimaryBg,
                  color: token.colorPrimary,
                  fontSize: 12,
                }}
              >
                {getInitials(payment.customerName)}
              </Avatar>
              <div>
                <Text strong style={{ fontSize: 13, display: 'block', lineHeight: 1.3 }}>
                  {payment.customerName}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  <MailOutlined style={{ marginRight: 4, fontSize: 10 }} />
                  {payment.customerEmail}
                </Text>
              </div>
            </Flex>
            <div style={{ textAlign: 'right' }}>
              <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>{t('refund.originalAmount')}</Text>
              <Text strong style={{ fontSize: 18, color: token.colorSuccess }}>
                {formatCurrency(payment.amount, payment.currency)}
              </Text>
            </div>
          </Flex>

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              amount: payment.amount,
              description: '',
            }}
            requiredMark={false}
          >
            <Row gutter={16}>
              {/* Refund amount input + quick-select percentage buttons */}
              <Col span={10}>
                <Form.Item
                  name="amount"
                  label={<Text strong style={{ fontSize: 12 }}>{t('refund.refundAmount')} ({payment.currency})</Text>}
                  rules={[
                    { required: true, message: t('refund.required') },
                    { type: 'number', min: 0.01, message: t('refund.minAmount') },
                    { type: 'number', max: payment.amount, message: t('refund.exceedsOriginal') },
                  ]}
                  style={{ marginBottom: 8 }}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0.01}
                    max={payment.amount}
                    step={0.01}
                    precision={2}
                    placeholder={t('refund.enterAmount')}
                    size="large"
                  />
                </Form.Item>

                {/* Quick percentage buttons (25 / 50 / 75 / 100 %) */}
                <Form.Item noStyle shouldUpdate>
                  {({ setFieldValue, getFieldValue }) => {
                    const currentAmount = getFieldValue('amount') || 0;

                    return (
                      <Flex gap={4}>
                        {REFUND_PERCENTAGE_OPTIONS.map((percent) => {
                          const amount = (payment.amount * percent) / 100;
                          const isActive = Math.abs(currentAmount - amount) < 0.01;
                          return (
                            <div
                              key={percent}
                              onClick={() => setFieldValue('amount', Number(amount.toFixed(2)))}
                              style={{
                                flex: 1,
                                textAlign: 'center',
                                padding: '5px 0',
                                borderRadius: token.borderRadiusSM,
                                border: `1px solid ${isActive ? token.colorPrimary : token.colorBorder}`,
                                background: isActive ? token.colorPrimaryBg : token.colorBgContainer,
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                              }}
                            >
                              <Text
                                strong
                                style={{
                                  fontSize: 12,
                                  color: isActive ? token.colorPrimary : token.colorText,
                                }}
                              >
                                {percent}%
                              </Text>
                            </div>
                          );
                        })}
                      </Flex>
                    );
                  }}
                </Form.Item>
              </Col>

              {/* Refund reason (required) */}
              <Col span={14}>
                <Form.Item
                  name="description"
                  label={<Text strong style={{ fontSize: 12 }}>{t('refund.refundReason')}</Text>}
                  rules={[
                    { required: true, message: t('refund.required') },
                    { min: 3, message: t('refund.minChars') },
                  ]}
                  style={{ marginBottom: 22 }}
                >
                  <TextArea
                    rows={4}
                    placeholder={t('refund.enterReason')}
                    maxLength={500}
                    showCount
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Live refund summary — partial vs full with progress bar */}
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => prev.amount !== curr.amount}
            >
              {({ getFieldValue }) => {
                const amount = getFieldValue('amount') || 0;
                const isPartial = amount < payment.amount;
                const percentage = (amount / payment.amount) * 100;

                return (
                  <div
                    style={{
                      background: isPartial ? token.colorInfoBg : token.colorWarningBg,
                      borderRadius: token.borderRadius,
                      padding: `${token.paddingXS}px ${token.paddingSM}px`,
                      marginTop: token.marginSM,
                    }}
                  >
                    <Flex justify="space-between" align="center">
                      <Flex align="center" gap={token.marginXS}>
                        <Text strong style={{ color: isPartial ? token.colorInfo : token.colorWarning, fontSize: 13 }}>
                          {isPartial ? t('refund.partialRefund') : t('refund.fullRefund')}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {isPartial
                            ? t('refund.customerKeeps', { amount: formatCurrency(payment.amount - amount, payment.currency) })
                            : t('refund.cannotBeUndone')}
                        </Text>
                      </Flex>
                      <Text
                        strong
                        style={{
                          fontSize: 18,
                          color: isPartial ? token.colorInfo : token.colorWarning,
                        }}
                      >
                        {formatCurrency(amount, payment.currency)}
                      </Text>
                    </Flex>
                    <Progress
                      percent={percentage}
                      showInfo={false}
                      strokeColor={isPartial ? token.colorInfo : token.colorWarning}
                      railColor={token.colorBgContainer}
                      size="small"
                      style={{ marginTop: 6, marginBottom: 0 }}
                    />
                  </div>
                );
              }}
            </Form.Item>
          </Form>

          {/* Irreversibility warning */}
          <Alert
            title={t('refund.warning')}
            type="warning"
            showIcon
            style={{ marginTop: token.marginSM }}
            banner
          />
        </div>
      )}
      <FormModalFooter
        onCancel={handleClose}
        onSubmit={handleSubmit}
        submitLabel={t('refund.processRefund')}
        loading={refundMutation.isPending}
        danger
        submitIcon={<RollbackOutlined />}
      />
    </Modal>
  );
}
